import {
  HostMessageType,
  POINTER_GUARD_EVENTS,
  PREVIEW_DEV_HOSTNAMES,
  PREVIEW_ORIGIN_SUFFIXES,
  PreviewDevice,
  PreviewInsert,
  PreviewMessageType,
  PreviewSource,
  PreviewZone,
  SubdomainType,
} from '@/constants'
import type { ConfigurablePage, MomentKey, PortalSection, PortalSettings, ThemeVariables } from '@/types'
import { getPreviewSubdomainType, isInteractivePreviewMode } from './previewMode'

export interface PreviewCampaign {
  id?: number | string
  name?: string
  subdomain?: string
  logo?: string
  mobile_logo?: string
  display_name?: string
  support_email?: string
  support_phone?: string
}

export interface HostInitPayload {
  subdomainType: SubdomainType
  campaign: PreviewCampaign
  theme: ThemeVariables
  portalSettings: PortalSettings
  page: ConfigurablePage
  moment: MomentKey
  device: PreviewDevice
  selectedUid: string | null
}

export interface HostConfigPayload {
  portalSettings: PortalSettings
}

export interface HostConfigPagePayload {
  page: ConfigurablePage
  moment: MomentKey
  sections: PortalSection[]
}

export interface HostThemePayload {
  theme: ThemeVariables
}

export interface HostPagePayload {
  page: ConfigurablePage
}

export interface HostMomentPayload {
  moment: MomentKey
}

export interface HostDevicePayload {
  device: PreviewDevice
}

export interface HostSelectPayload {
  uid: string | null
  reveal?: boolean
}

export interface HostHoverPayload {
  uid: string | null
}

// Editor-driven: with it on, the preview's own nav links and Manage buttons work.
export interface HostNavigationPayload {
  enabled: boolean
}

export interface PreviewFaqTopic {
  id: number
  title: string
}

export interface PreviewFaq {
  id: number
  question: string
  answer: string
}

export interface HostFaqsPayload {
  topics: PreviewFaqTopic[]
  faqs: PreviewFaq[]
}

export interface HostPayloads {
  [HostMessageType.Hello]: Record<string, never>
  [HostMessageType.Init]: HostInitPayload
  [HostMessageType.Config]: HostConfigPayload
  [HostMessageType.ConfigPage]: HostConfigPagePayload
  [HostMessageType.Theme]: HostThemePayload
  [HostMessageType.Page]: HostPagePayload
  [HostMessageType.Moment]: HostMomentPayload
  [HostMessageType.Device]: HostDevicePayload
  [HostMessageType.Select]: HostSelectPayload
  [HostMessageType.Hover]: HostHoverPayload
  [HostMessageType.Faqs]: HostFaqsPayload
  [HostMessageType.Navigation]: HostNavigationPayload
}

export interface PreviewReadyPayload {
  subdomainType: SubdomainType | null
}

// Sent when the preview navigates itself, so the editor's Page dropdown follows.
export interface PreviewNavigatePayload {
  page: ConfigurablePage
}

export interface PreviewSelectPayload {
  page: ConfigurablePage
  moment: MomentKey
  uid: string
  sectionKey: string
  index: number
  childIndex?: number
}

export interface PreviewAddRequestPayload {
  page: ConfigurablePage
  moment: MomentKey
  // Anchored on the clicked section, not a positional index: the rendered array
  // is not the stored one on the list pages.
  anchorUid: string
  insert: PreviewInsert
  zone: PreviewZone
}

export interface PreviewReorderPayload {
  page: ConfigurablePage
  moment: MomentKey
  order: string[]
  basedOnSeq: number
}

export interface PreviewDeleteRequestPayload {
  page: ConfigurablePage
  moment: MomentKey
  uid: string
}

export interface PreviewPayloads {
  [PreviewMessageType.Ready]: PreviewReadyPayload
  [PreviewMessageType.Navigate]: PreviewNavigatePayload
  [PreviewMessageType.Select]: PreviewSelectPayload
  [PreviewMessageType.AddRequest]: PreviewAddRequestPayload
  [PreviewMessageType.Reorder]: PreviewReorderPayload
  [PreviewMessageType.DeleteRequest]: PreviewDeleteRequestPayload
}

interface Envelope {
  source: string
  seq: number
  type: string
  payload: unknown
}

type HostHandler<T extends HostMessageType> = (payload: HostPayloads[T], seq: number) => void

const handlers = new Map<HostMessageType, Set<HostHandler<never>>>()

const COALESCED: HostMessageType[] = [HostMessageType.Config, HostMessageType.ConfigPage, HostMessageType.Theme]

let hostOrigin: string | null = null
let lastAppliedSeq = 0
let pending: Map<HostMessageType, { payload: unknown; seq: number }> | null = null
let frame = 0

export function getHostOrigin(): string | null {
  return hostOrigin
}

export function getLastAppliedSeq(): number {
  return lastAppliedSeq
}

export function isTrustedHostOrigin(origin: string): boolean {
  let url: URL
  try {
    url = new URL(origin)
  } catch {
    return false
  }
  if (import.meta.env.DEV && PREVIEW_DEV_HOSTNAMES.includes(url.hostname)) {
    return true
  }
  return PREVIEW_ORIGIN_SUFFIXES.some(suffix => url.hostname.endsWith(suffix))
}

// The preview module is a dynamic import, so a message can arrive before it subscribes.
const undelivered = new Map<HostMessageType, { payload: unknown; seq: number }>()

export function onHostMessage<T extends HostMessageType>(type: T, handler: HostHandler<T>): () => void {
  const set = handlers.get(type) ?? new Set()
  set.add(handler as HostHandler<never>)
  handlers.set(type, set)
  const missed = undelivered.get(type)
  if (missed) {
    undelivered.delete(type)
    ;(handler as (p: unknown, s: number) => void)(missed.payload, missed.seq)
  }
  return () => {
    set.delete(handler as HostHandler<never>)
  }
}

export function sendToHost<T extends PreviewMessageType>(type: T, payload: PreviewPayloads[T]): void {
  if (typeof window === 'undefined' || window.parent === window) {
    return
  }
  const envelope: Envelope = {
    source: PreviewSource.Preview,
    seq: lastAppliedSeq,
    type,
    payload,
  }
  // '*' only pre-handshake, when the message carries nothing but the version.
  window.parent.postMessage(envelope, hostOrigin ?? '*')
}

function dispatch(type: HostMessageType, payload: unknown, seq: number): void {
  const set = handlers.get(type)
  if (!set || set.size === 0) {
    undelivered.set(type, { payload, seq })
    return
  }
  set.forEach(handler => (handler as (p: unknown, s: number) => void)(payload, seq))
}

function flush(): void {
  frame = 0
  const batch = pending
  pending = null
  if (!batch) {
    return
  }
  batch.forEach((entry, type) => dispatch(type, entry.payload, entry.seq))
}

function queue(type: HostMessageType, payload: unknown, seq: number): void {
  if (!pending) {
    pending = new Map()
  }
  pending.set(type, { payload, seq })
  if (!frame) {
    frame = window.requestAnimationFrame(flush)
  }
}

function parse(event: MessageEvent): Envelope | null {
  const data = event.data as Envelope | undefined
  if (!data || data.source !== PreviewSource.Host) {
    return null
  }
  if (event.source !== window.parent) {
    return null
  }
  if (hostOrigin != null) {
    if (event.origin !== hostOrigin) {
      return null
    }
  } else if (!isTrustedHostOrigin(event.origin)) {
    return null
  }
  if (typeof data.type !== 'string' || !Object.values(HostMessageType).includes(data.type as HostMessageType)) {
    return null
  }
  return data
}

// Every click but ours would fire a real portal action.
let onSelect: ((uid: string, key: string, index: number) => void) | null = null
let onHover: ((uid: string | null) => void) | null = null

// Set by the portal's preview module: utils must not know a portal's class names.
let interactiveSelector: string | null = null
let navigationSelector: string | null = null

export function setPreviewInteractiveSelector(selector: string | null): void {
  interactiveSelector = selector
}

export function setPreviewNavigationSelector(selector: string | null): void {
  navigationSelector = selector
}

export function setPreviewInteractionHandlers(
  select: (uid: string, key: string, index: number) => void,
  hover: (uid: string | null) => void
): void {
  onSelect = select
  onHover = hover
}

function frameOf(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) {
    return null
  }
  return target.closest('[data-preview-section]')
}

function selectFrom(event: Event): void {
  if (event.type === 'click' && onSelect) {
    const frame = frameOf(event.target)
    if (frame) {
      onSelect(
        frame.dataset.previewSection || '',
        frame.dataset.previewKey || '',
        Number(frame.dataset.previewIndex || 0)
      )
    }
  }
}

function setupInteractionGuard(): () => void {
  const swallow = (event: Event) => {
    const target = event.target as Element | null
    // Our own + / grip controls must keep working.
    if (target instanceof Element && target.closest('[data-preview-ui]')) {
      return
    }
    if (navigationSelector && target instanceof Element && target.closest(navigationSelector)) {
      return
    }
    // Controls the portal marked safe to drive here: they select their section and still run.
    if (interactiveSelector && target instanceof Element && target.closest(interactiveSelector)) {
      selectFrom(event)
      return
    }
    event.stopPropagation()
    event.preventDefault()
    selectFrom(event)
  }
  POINTER_GUARD_EVENTS.forEach(type => document.addEventListener(type, swallow, true))

  const track = (event: Event) => {
    if (!onHover) {
      return
    }
    const frame = frameOf(event.target)
    onHover(frame ? frame.dataset.previewSection || null : null)
  }
  document.addEventListener('pointerover', track, { passive: true })

  return () => {
    POINTER_GUARD_EVENTS.forEach(type => document.removeEventListener(type, swallow, true))
    document.removeEventListener('pointerover', track)
  }
}

function announceReady(): void {
  sendToHost(PreviewMessageType.Ready, { subdomainType: getPreviewSubdomainType() })
}

export function setupPreviewBridge(): () => void {
  if (!isInteractivePreviewMode() || typeof window === 'undefined') {
    return () => {}
  }

  const onMessage = (event: MessageEvent) => {
    const envelope = parse(event)
    if (!envelope) {
      return
    }

    // Pin the origin on the first valid message; every later one must match.
    hostOrigin = event.origin

    const type = envelope.type as HostMessageType
    if (type === HostMessageType.Hello) {
      announceReady()
      return
    }

    // A late echo of our own optimistic change; dropping it stops the ping-pong.
    if (envelope.seq < lastAppliedSeq) {
      return
    }
    lastAppliedSeq = envelope.seq

    if (COALESCED.includes(type)) {
      queue(type, envelope.payload, envelope.seq)
      return
    }
    dispatch(type, envelope.payload, envelope.seq)
  }

  window.addEventListener('message', onMessage)
  const teardownGuard = setupInteractionGuard()
  // If the host is not listening yet, its `hello` ping gets us a second chance.
  announceReady()

  return () => {
    window.removeEventListener('message', onMessage)
    teardownGuard()
    if (frame) {
      window.cancelAnimationFrame(frame)
      frame = 0
    }
    pending = null
  }
}
